import { useEffect, useState } from "react";
import { useT } from "../../features/i18n/useT";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  action?: ToastAction;
  duration?: number;
  tone?: "success" | "danger" | "info";
}

interface ToastProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

export function ToastComponent({ toast, onDismiss }: ToastProps) {
  const t = useT();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const duration = toast.duration ?? 4000;
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onDismiss(toast.id), 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  const tone = toast.tone ? `toast-${toast.tone}` : "";
  return (
    <div className={`toast ${tone} ${isVisible ? "is-visible" : ""}`}>
      <div className="toast-message">{toast.message}</div>
      {toast.action && (
        <button className="toast-action" onClick={toast.action.onClick}>
          {toast.action.label}
        </button>
      )}
      <button className="toast-close" onClick={() => onDismiss(toast.id)} aria-label={t("common.close")}>
        x
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <ToastComponent key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// Hook for managing toasts
let toastIdCounter = 0;
const toastListeners = new Set<(toasts: Toast[]) => void>();
let toastState: Toast[] = [];

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>(toastState);

  useEffect(() => {
    const listener = (newToasts: Toast[]) => setToasts(newToasts);
    toastListeners.add(listener);
    return () => {
      toastListeners.delete(listener);
    };
  }, []);

  const show = (message: string, action?: ToastAction, duration?: number, tone?: Toast["tone"]) => {
    const id = `toast-${++toastIdCounter}`;
    const newToast: Toast = { id, message, action, duration, tone };
    toastState = [...toastState, newToast];
    toastListeners.forEach((l) => l(toastState));
  };

  const dismiss = (id: string) => {
    toastState = toastState.filter((t) => t.id !== id);
    toastListeners.forEach((l) => l(toastState));
  };

  return { toasts, show, dismiss };
}
